import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { userService, UserProfile, type AccountHealth } from '@/services/userService';
import { useAuth } from '@/hooks/useAuth';
import { authService } from '@/services/authService';
import {
  registerUserProfileUpdateCallback,
  unregisterUserProfileUpdateCallback,
} from '@/services/httpClient';

interface UserProfileData {
  name: string;
  email: string;
  timezone: string;
  avatar?: string;
  accountHealth?: AccountHealth | null;
  /** FEL-57 · Count of channels tracked by the user's account (from /api/users/me). */
  channelsTracked: number;
  isDemo?: boolean;
}

interface UserProfileContextType {
  userData: UserProfileData;
  isLoading: boolean;
  error: string | null;
  refetchProfile: () => Promise<void>;
}

const UserProfileContext = createContext<UserProfileContextType | undefined>(undefined);

// Global cache to prevent multiple calls
let cachedUserData: UserProfileData | null = null;
let isCurrentlyFetching = false;
let globalRefetchCallback: (() => Promise<void>) | null = null;

// Export function to trigger refetch from anywhere
export const triggerProfileRefetch = async (): Promise<void> => {
  if (globalRefetchCallback) {
    await globalRefetchCallback();
  }
};

// Export function to clear cached user profile data (used on logout)
export const clearUserProfileCache = (): void => {
  cachedUserData = null;
  isCurrentlyFetching = false;
  // Note: globalRefetchCallback is NOT cleared here so that
  // triggerProfileRefetch() still works after logout→login cycles.
};

export const UserProfileProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  // Stable ref so fetchUserData doesn't need `t` in its deps (which would
  // re-trigger the fetch effect on every language switch). Placeholder
  // strings (loading / error states) are transient, so a stale translation
  // until the next state update is acceptable.
  const tRef = useRef(t);
  tRef.current = t;

  const [userData, setUserData] = useState<UserProfileData>(() => {
    return (
      cachedUserData || {
        name: t('app.common.loading'),
        email: '',
        timezone: 'UTC',
        channelsTracked: 0,
      }
    );
  });
  const [isLoading, setIsLoading] = useState(true); // Always start as loading to ensure fetch
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated } = useAuth();
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Function to update user data from external source (HTTP interceptor)
  const updateUserDataFromProfile = useCallback((profile: UserProfile) => {
    if (!profile || !profile.id || !profile.name || !profile.email) {
      return;
    }

    const newUserData = {
      name: profile.name,
      email: profile.email,
      timezone: profile.timezone || 'UTC',
      avatar: profile.avatarUrl || undefined,
      accountHealth: profile.accountHealth ?? null,
      channelsTracked: profile.channelsTracked ?? 0,
      isDemo: profile.isDemo ?? false,
    };

    cachedUserData = newUserData;

    if (isMountedRef.current) {
      setUserData({ ...newUserData });
      setError(null);
      setIsLoading(false);
    }
  }, []);

  const fetchUserData = useCallback(
    async (force = false) => {
      // When force=true (after login), check token directly instead of relying on stale React state
      const hasValidToken = force ? authService.isAuthenticated() : isAuthenticated;

      if (!hasValidToken) {
        cachedUserData = null;
        setUserData({
          name: tRef.current('app.common.user'),
          email: 'user@example.com',
          timezone: 'UTC',
          avatar: undefined,
          channelsTracked: 0,
        });
        setIsLoading(false);
        return;
      }

      // If already fetching and not forced, skip
      if (isCurrentlyFetching && !force) {
        return;
      }

      try {
        setIsLoading(true);
        isCurrentlyFetching = true;

        // Clear cache when force is true (after login)
        if (force) {
          cachedUserData = null;
        }

        const profile = await userService.getProfile();

        if (profile && profile.id && profile.name && profile.email) {
          const newUserData = {
            name: profile.name,
            email: profile.email,
            timezone: profile.timezone || 'UTC',
            avatar: profile.avatarUrl || undefined,
            accountHealth: profile.accountHealth ?? null,
            channelsTracked: profile.channelsTracked ?? 0,
            isDemo: profile.isDemo ?? false,
          };

          cachedUserData = newUserData;

          if (isMountedRef.current) {
            setUserData({ ...newUserData });
            setError(null);
          }
        } else {
          throw new Error('Invalid profile data received');
        }
      } catch (err) {
        console.error('Failed to fetch user profile:', err);
        const fallbackData = {
          name: tRef.current('app.common.profileLoadError'),
          email: 'error@example.com',
          timezone: 'UTC',
          avatar: undefined,
          channelsTracked: 0,
        };

        if (isMountedRef.current) {
          setUserData(fallbackData);
          setError(tRef.current('app.common.profileLoadFailed'));
        }
      } finally {
        isCurrentlyFetching = false;
        if (isMountedRef.current) {
          setIsLoading(false);
        }
      }
    },
    [isAuthenticated]
  );

  // Handle authentication state changes (single unified useEffect)
  useEffect(() => {
    // Clear state when logged out
    if (!isAuthenticated) {
      cachedUserData = null;
      isCurrentlyFetching = false;
      setUserData({
        name: tRef.current('app.common.user'),
        email: 'user@example.com',
        timezone: 'UTC',
        avatar: undefined,
        channelsTracked: 0,
      });
      setIsLoading(false);
      return;
    }

    // Fetch user data when authenticated
    // Call directly - the token should already be set when isAuthenticated becomes true
    fetchUserData(true);
  }, [isAuthenticated, fetchUserData]);

  const refetchProfile = useCallback(async () => {
    // Check token directly instead of relying on potentially stale React state
    if (!authService.isAuthenticated()) {
      return;
    }

    // Force refetch to bypass cache
    await fetchUserData(true);
  }, [fetchUserData]);

  // Register global refetch callback
  useEffect(() => {
    globalRefetchCallback = refetchProfile;
    return () => {
      globalRefetchCallback = null;
    };
  }, [refetchProfile]);

  // Register HTTP interceptor callback to auto-update on /api/users/me calls
  useEffect(() => {
    registerUserProfileUpdateCallback(updateUserDataFromProfile);
    return () => {
      unregisterUserProfileUpdateCallback();
    };
  }, [updateUserDataFromProfile]);

  return (
    <UserProfileContext.Provider value={{ userData, isLoading, error, refetchProfile }}>
      {children}
    </UserProfileContext.Provider>
  );
};

export const useUserProfile = () => {
  const context = useContext(UserProfileContext);
  if (context === undefined) {
    throw new Error('useUserProfile must be used within a UserProfileProvider');
  }
  return context;
};
